import { useEffect } from 'react';
import { showErrorToast } from '@app/components/CommonToast';
import { loadingScreen } from '@app/components/Loading';
import { MASTER_CODE_TYPE } from '@app/consts/master_code_consts';
import { setRole } from '@app/features/rolesSlice';
import { MessageUtils } from '@app/modules/errors';
import { commonApiService } from '@app/services/commonApi';
import { MasterCodeItem } from '@app/types/initScreen';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

interface useTopBarProps {
  enabled?: boolean;
}

const toUpperSnakeCase = (str: string): string => {
  return str
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .toUpperCase();
};

/**
 * Convert MasterCodeItem[] to Record<string, string>
 * Example: [{ code: "001", nameEN: "Admin" }] -> { ADMIN: "001" }
 */
const convertToRoleRecord = (items: MasterCodeItem[]): Record<string, string> => {
  return items.reduce(
    (acc, item) => {
      const key = toUpperSnakeCase(item.nameEN);
      if (key) {
        acc[key] = item.code;
      }
      return acc;
    },
    {} as Record<string, string>
  );
};

export function useTopBar({ enabled = true }: useTopBarProps = {}): useTopBarProps {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const fetchUserRoles = async () => {
      try {
        loadingScreen.show();
        // Call API to get user roles
        const response = await commonApiService.getMasterCodeOptions({
          masterTypeCodes: [
            MASTER_CODE_TYPE.SYSTEM_ROLE_CODE,
            MASTER_CODE_TYPE.PROJECT_ROLE_CODE,
            MASTER_CODE_TYPE.PROFESSION_ROLE_CODE,
          ],
        });
        if (response?.success && response.data) {
          const data = response.data.content;

          // Combine all role types into one object
          const combinedRoles: Record<string, string> = {};
          Object.values(data).forEach((items: MasterCodeItem[]) => {
            const converted = convertToRoleRecord(items);
            Object.assign(combinedRoles, converted);
          });

          // Dispatch to Redux store
          dispatch(setRole(combinedRoles));
        }
      } catch (error: any) {
        showErrorToast(MessageUtils.formatApiMessage(error) ?? t(error?.code, { ns: 'messages' }));
      } finally {
        loadingScreen.hide();
      }
    };

    fetchUserRoles();
  }, [enabled, t, dispatch]);

  return {};
}
